import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../../utils/cmsAuth";
import useUploadSuccessAlert from "../useUploadSuccessAlert";
import "./YoutubeLab.css";

const BASE = "/concierge/youtube-lab";
const RUNNING = new Set(["QUEUED", "PREPARING", "UPLOADING"]);
const PROCESSING_DONE = new Set(["succeeded", "failed", "rejected", "terminated"]);
const isPublished = (job) => job.status === "UPLOADED" && Boolean(job.youtube_video_id)
  && job.actual_privacy === "public" && job.processing_status === "succeeded";
const LABELS = { QUEUED: "접수 완료", PREPARING: "영상 확인·AI 해시태그 생성 중", UPLOADING: "유튜브 전송 중",
  UPLOADED: "영상 전송 완료", FAILED: "게시 전 준비 실패", NEEDS_REVIEW: "게시 여부 확인 필요", REVIEWED: "관리자 확인 완료" };

async function api(path, options = {}) {
  const response = await authFetch(`${BASE}${path}`, {
    ...options, headers: { "Content-Type": "application/json", ...options.headers },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
  return data;
}

function requestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error("안전한 요청 ID를 만들 수 없습니다. HTTPS 주소와 최신 브라우저를 사용해 주세요.");
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function when(value) {
  return value ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "-";
}

function status(job) {
  if (["failed", "rejected", "terminated"].includes(job.processing_status)) return "유튜브 영상 처리 실패";
  if (job.status === "UPLOADED" && job.actual_privacy === "public" && job.processing_status === "succeeded") return "공개·처리 완료";
  return LABELS[job.status] || job.status;
}

function Thumbnail({ videoId }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl;
    setSrc("");
    authFetch(`${BASE}/videos/${videoId}/thumbnail`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return;
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      }).catch(() => {});
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [videoId]);
  return src ? <img src={src} alt="" loading="lazy" /> : <span className="cyl-thumbnail-empty">썸네일 준비 중 / 미리보기 없음</span>;
}

export default function YoutubeLab() {
  const [config, setConfig] = useState(null);
  const [gallery, setGallery] = useState({ items: [], total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const pending = useRef(null);
  const submitting = useRef(false);
  const alive = useRef(true);
  const trackSuccess = useUploadSuccessAlert(job, isPublished, "유튜브");

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    api("/configuration", { signal: controller.signal }).then((data) => setConfig(data))
      .catch((e) => { if (!controller.signal.aborted) setError(e.message); });
    return () => { alive.current = false; controller.abort(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api("/jobs", { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) setJobs(data.items || []);
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [revision]);

  useEffect(() => {
    const controller = new AbortController();
    setGalleryLoading(true);
    api(`/media?page=${page}&page_size=24`, { signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) {
        setGallery(data);
        if (data.page && data.page !== page) setPage(data.page);
      }
    }).catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setGalleryLoading(false); });
    return () => controller.abort();
  }, [page]);

  useEffect(() => {
    if (!job || (!RUNNING.has(job.status) && !(job.status === "UPLOADED" && job.youtube_video_id
      && (!PROCESSING_DONE.has(job.processing_status) || !job.actual_privacy)))) return undefined;
    const controller = new AbortController();
    let fetching = false;
    const timer = setInterval(() => {
      if (fetching) return;
      fetching = true;
      const query = job.youtube_video_id ? `/jobs/${job.request_id}/refresh` : `/jobs/${job.request_id}`;
      api(query, { signal: controller.signal, ...(job.youtube_video_id ? { method: "POST" } : {}) }).then((data) => {
        if (!controller.signal.aborted) { setJob(data); setRevision((n) => n + 1); }
      }).catch((e) => { if (!controller.signal.aborted) setError(`상태 조회 실패: ${e.message} 업로드 실패로 확정된 것은 아닙니다. 다시 조회합니다.`); })
        .finally(() => { fetching = false; });
    }, 4000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [job]);

  useEffect(() => {
    if (job?.status === "FAILED" || job?.status === "REVIEWED") pending.current = null;
  }, [job]);

  async function start() {
    if (submitting.current || !selectedVideo || !config?.ready) return;
    if (!window.confirm(`테스트 채널 '${config.channel_name}'에 '${selectedVideo.store_name}' 영상을 지금 공개 업로드합니다. 진행할까요?`)) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      if (!pending.current) pending.current = requestId();
      trackSuccess(pending.current);
      const data = await api("/jobs", { method: "POST", body: JSON.stringify({
        request_id: pending.current, user_id: selectedVideo.user_id, video_id: Number(selectedVideo.video_id), confirm_public: true,
      }) });
      if (alive.current) { trackSuccess(data.request_id); setJob(data); setRevision((n) => n + 1); }
    } catch (e) {
      if (alive.current) { setError(`${e.message} 접수 여부가 불확실하면 같은 영상으로 다시 누르거나 기록을 확인하세요. 같은 요청 ID를 재사용합니다.`); setRevision((n) => n + 1); }
    } finally { submitting.current = false; if (alive.current) setBusy(false); }
  }

  async function refresh(record) {
    setBusy(true); setError("");
    try {
      const data = await api(`/jobs/${record.request_id}/refresh`, { method: "POST" });
      if (alive.current) { setJob(data); setRevision((n) => n + 1); }
    } catch (e) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }

  async function review() {
    if (!window.confirm("YouTube Studio에서 이 영상이 게시되지 않았음을 직접 확인했습니까? 잘못 해제하면 중복 게시될 수 있습니다.")) return;
    setBusy(true);
    try {
      const data = await api(`/jobs/${job.request_id}/review`, { method: "POST", body: JSON.stringify({ confirmed_studio_review: true }) });
      if (alive.current) { setJob(data); pending.current = null; setRevision((n) => n + 1); }
    } catch (e) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  }

  const active = busy || (job && RUNNING.has(job.status));
  const previewBase = process.env.REACT_APP_FASTAPI_ADS_URL || process.env.REACT_APP_FASTAPI_SEVER_URL || "";

  return <main className="cyl-page">
    <header className="cyl-heading"><div><span>YOUTUBE UPLOAD LAB</span><h1>유튜브 자동업로드 테스트머신</h1>
      <p>저장된 영상 선택 → AI 해시태그 생성 → 공개 업로드. 인스타그램·매장 승인·문자 발송과 별도 실행.</p></div><b>SHORTS ONLY</b></header>
    <section className="cyl-panel"><h2>테스트 채널</h2><p>{config?.channel_name || "설정 확인 중"} {config?.channel_id && `· ${config.channel_id}`}</p>
      {config && !config.ready && <p role="alert">{config.message}</p>}
      <small>쇼츠 조건: 정사각형·세로, 최대 3분. 실제 Shorts 분류는 YouTube에서 확인. 음원 저작권·API 앱 검증에 따라 공개가 제한될 수 있습니다.</small></section>
    {error && <p className="cyl-error" role="alert">{error}</p>}
    <section className="cyl-panel"><div className="cyl-result-heading"><div><h2>업로드할 영상 선택</h2><small>매장명과 썸네일을 보고 영상 1개를 선택하세요. 총 {gallery.total}개</small></div>
      <button className="cyl-primary" type="button" disabled={Boolean(active) || galleryLoading || !selectedVideo || !config?.ready} onClick={start}>{busy ? "접수 확인 중…" : "자동업로드 실행"}</button></div>
      {galleryLoading ? <p role="status">영상 목록을 불러오는 중…</p> : !gallery.items.length ? <p>CMS에 업로드된 동영상 파일이 없습니다. 외부 원본 URL은 자동 다운로드하지 않습니다.</p> :
        <div className="cyl-video-grid">{gallery.items.map((item) => <button type="button" key={item.video_id}
          className="cyl-video-card" aria-pressed={selectedVideo?.video_id === item.video_id} disabled={Boolean(active)}
          aria-label={`${item.store_name} ${item.original_name} 선택`} title={item.original_name}
          onClick={() => { setSelectedVideo(item); pending.current = null; }}>
          <div className="cyl-thumbnail"><Thumbnail videoId={item.video_id} /><span>{Math.round(item.duration_seconds || 0)}초</span>
            {selectedVideo?.video_id === item.video_id && <b>선택됨</b>}</div><strong>{item.store_name}</strong>
        </button>)}</div>}
      {gallery.total_pages > 1 && <nav className="cyl-pagination" aria-label="영상 목록 페이지">
        <button type="button" disabled={Boolean(active) || galleryLoading || page <= 1} onClick={() => { setPage(page - 1); setSelectedVideo(null); pending.current = null; }}>이전 영상</button>
        <span>{page} / {gallery.total_pages}</span>
        <button type="button" disabled={Boolean(active) || galleryLoading || page >= gallery.total_pages} onClick={() => { setPage(page + 1); setSelectedVideo(null); pending.current = null; }}>다음 영상</button>
      </nav>}
      {selectedVideo && <div className="cyl-preview"><video controls preload="metadata" src={`${previewBase}/concierge/videos/${selectedVideo.video_id}/file`} aria-label="선택 영상 미리보기" />
        <div><h3>선택한 영상</h3><dl><dt>제목</dt><dd>{selectedVideo.store_name}</dd><dt>영상</dt><dd>{selectedVideo.original_name}</dd><dt>설명</dt><dd>AI 해시태그만 자동 입력</dd><dt>공개 범위</dt><dd>공개 · 구독자 알림 끔</dd></dl></div></div>}
    </section>
    {job && <section className="cyl-panel" aria-label="테스트 결과"><div className="cyl-result-heading"><h2>{status(job)}</h2><button type="button" disabled={busy} onClick={() => refresh(job)}>상태 다시 확인</button></div>
      <p>{job.store_name} · {when(job.created_at)}</p><p>제목: {job.title}</p><p>해시태그: {job.hashtags || "생성 전"}</p>
      <p>실제 공개 상태: {job.actual_privacy || "확인 전"} · 영상 처리: {job.processing_status || "확인 전"}</p>
      {job.actual_privacy && job.actual_privacy !== "public" && <p className="cyl-error">공개 요청과 실제 공개 상태가 다릅니다. YouTube Studio에서 제한 사유를 확인해 주세요.</p>}
      {job.error_message && <p className="cyl-error">{job.error_message}</p>}
      {job.shorts_url && <a href={job.shorts_url} target="_blank" rel="noopener noreferrer">YouTube에서 쇼츠 형태 확인 ↗</a>}
      {!job.youtube_video_id && (RUNNING.has(job.status) || job.status === "NEEDS_REVIEW") && <div><small>서버 재시작·연결 중단으로 멈춘 경우 Studio에서 게시 여부 확인 후에만 해제하세요. 실제 진행 중인 작업은 해제할 수 없습니다.</small><button type="button" disabled={busy} onClick={review}>Studio 확인 후 재시도 허용</button></div>}
    </section>}
    <section className="cyl-panel"><div className="cyl-result-heading"><h2>최근 테스트 20건</h2><button type="button" onClick={() => setRevision((n) => n + 1)}>기록 새로고침</button></div>
      {!jobs.length ? <p>테스트 기록 없음</p> : <div className="cyl-history">{jobs.map((item) => <button type="button" key={item.request_id} onClick={() => setJob(item)}><strong>{item.store_name}</strong><span>{status(item)}</span><small>{when(item.created_at)}</small></button>)}</div>}
    </section>
  </main>;
}
